import { Menu, X } from "lucide-react";
import { AnimatePresence, motion, useMotionValueEvent, useReducedMotion, useScroll } from "motion/react";
import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { NavLink, useLocation } from "react-router";
import { CtaAnchor } from "@/src/common/components/cta-link";
import { APP_LINKS } from "@/src/common/constants/app-links";
import { SITE_CONFIG } from "@/src/common/constants/site-config";
import { MEDIA_MD, useMediaQuery } from "@/src/common/hooks/use-media-query";
import { LanguageSwitcher } from "./language-switcher";
import { SiteLogo } from "./site-logo";

const HIDE_AFTER_PX = 120;

interface MenuState {
	pathname: string;
	open: boolean;
}

export function SiteHeader() {
	const { t } = useTranslation("common");
	const [scrolled, setScrolled] = useState(false);
	const [hidden, setHidden] = useState(false);
	const location = useLocation();
	const [menu, setMenu] = useState<MenuState>({ pathname: location.pathname, open: false });
	const mobileOpen = menu.open && menu.pathname === location.pathname;
	const menuId = useId();
	const toggleRef = useRef<HTMLButtonElement>(null);
	const menuRef = useRef<HTMLDivElement>(null);
	const isDesktop = useMediaQuery(MEDIA_MD);
	const reduced = useReducedMotion() === true;
	const { scrollY } = useScroll();

	const setMobileOpen = (open: boolean) => setMenu({ pathname: location.pathname, open });

	useMotionValueEvent(scrollY, "change", (latest) => {
		const previous = scrollY.getPrevious() ?? latest;
		setScrolled(latest > 8);
		setHidden(isDesktop && !reduced && !mobileOpen && latest > HIDE_AFTER_PX && latest > previous);
	});

	useEffect(() => {
		setScrolled(window.scrollY > 8);
	}, []);

	useEffect(() => {
		if (!mobileOpen) return;
		const menuNode = menuRef.current;
		const toggleNode = toggleRef.current;
		menuNode?.querySelector<HTMLElement>("a, button, select")?.focus();
		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === "Escape") setMenu({ pathname: location.pathname, open: false });
		};
		document.addEventListener("keydown", handleKeyDown);
		return () => {
			document.removeEventListener("keydown", handleKeyDown);
			if (menuNode?.contains(document.activeElement)) toggleNode?.focus();
		};
	}, [mobileOpen, location.pathname]);

	return (
		<motion.header
			initial={false}
			animate={{ y: hidden ? "-100%" : "0%" }}
			transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
			className={`sticky top-0 z-40 w-full transition-[background-color,border-color,backdrop-filter] duration-300 ${
				scrolled ? "border-b border-default-200/60 bg-background/80 backdrop-blur-xl" : "border-b border-transparent bg-transparent"
			}`}
		>
			<div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
				<SiteLogo />

				<nav aria-label="Main" className="hidden items-center gap-1 md:flex">
					{SITE_CONFIG.primaryNav.map((item) => (
						<NavLink
							key={item.to}
							to={item.to}
							className={({ isActive }) =>
								`relative rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
									isActive ? "text-foreground" : "text-default-600 hover:bg-content1 hover:text-foreground"
								}`
							}
						>
							{({ isActive }) => (
								<>
									{isActive ? (
										<motion.span
											layoutId="nav-active"
											aria-hidden="true"
											className="absolute inset-0 rounded-full bg-content2"
											transition={{ type: "spring", stiffness: 420, damping: 34 }}
										/>
									) : null}
									<span className="relative z-10">{t(`nav.${item.key}`)}</span>
								</>
							)}
						</NavLink>
					))}
				</nav>

				<div className="hidden items-center gap-3 md:flex">
					<a
						href={APP_LINKS.SIGN_IN}
						className="rounded-full px-3 py-1.5 text-sm font-medium text-default-600 transition-colors hover:text-foreground"
					>
						{t("nav.sign_in")}
					</a>
					<CtaAnchor href={APP_LINKS.SIGN_UP} variant="gradient" size="sm">
						{t("nav.sign_up_free")}
					</CtaAnchor>
				</div>

				<button
					ref={toggleRef}
					type="button"
					aria-label={mobileOpen ? t("nav.close_menu") : t("nav.open_menu")}
					aria-expanded={mobileOpen}
					aria-controls={menuId}
					onClick={() => setMobileOpen(!mobileOpen)}
					className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-default-200 bg-content1 text-foreground md:hidden"
				>
					{mobileOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
				</button>
			</div>

			<AnimatePresence initial={false}>
				{mobileOpen ? (
					<motion.div
						key="mobile-menu"
						id={menuId}
						ref={menuRef}
						initial={reduced ? { opacity: 1 } : { opacity: 0, y: -8 }}
						animate={{ opacity: 1, y: 0 }}
						exit={reduced ? { opacity: 0 } : { opacity: 0, y: -8 }}
						transition={{ duration: 0.2, ease: "easeOut" }}
						className="border-t border-default-200/60 bg-background/95 backdrop-blur-xl md:hidden"
					>
						<nav aria-label="Mobile" className="mx-auto flex max-w-7xl flex-col gap-1 px-4 py-4 sm:px-6">
							{SITE_CONFIG.primaryNav.map((item) => (
								<NavLink
									key={item.to}
									to={item.to}
									className={({ isActive }) =>
										`rounded-xl px-3 py-2 text-sm font-medium transition-colors ${
											isActive ? "bg-content2 text-foreground" : "text-default-600 hover:bg-content1 hover:text-foreground"
										}`
									}
								>
									{t(`nav.${item.key}`)}
								</NavLink>
							))}
							<div className="mt-3 flex flex-col gap-3 border-t border-default-200/60 pt-3">
								<div className="flex items-center gap-3">
									<LanguageSwitcher />
									<a
										href={APP_LINKS.SIGN_IN}
										className="ml-auto rounded-full px-3 py-1.5 text-sm font-medium text-default-600 transition-colors hover:text-foreground"
									>
										{t("nav.sign_in")}
									</a>
								</div>
								<CtaAnchor href={APP_LINKS.SIGN_UP} variant="gradient" size="sm" className="w-full">
									{t("nav.sign_up_free")}
								</CtaAnchor>
							</div>
						</nav>
					</motion.div>
				) : null}
			</AnimatePresence>
		</motion.header>
	);
}
